import { useEffect, useState } from 'react';
import { IconPlus, IconPencil, IconTrash, IconStar, IconStarFilled } from '@tabler/icons-react';
import { Dialog } from '@mui/material';
import { IconX } from '@tabler/icons-react';
import { useForm, yupResolver } from '@mantine/form';
import * as yup from 'yup';
import PropTypes from 'prop-types';

import { Button, IconButton, Input } from 'ui';

import { useAccount } from '../../store/account/hooks';

const formValidation = yup.object({
  name: yup.string().required('Name is required'),
  url: yup.string().url('Must be a valid URL').required('URL is required'),
});

const EmbeddedDonationPageModal = ({ open, initialValues, onClose, onConfirm, loading }) => {
  const form = useForm({
    initialValues: initialValues || { name: '', url: '' },
    validate: yupResolver(formValidation),
    validateInputOnChange: true,
  });

  useEffect(() => {
    if (initialValues) {
      form.setValues(initialValues);
    } else {
      form.setValues({ name: '', url: '' });
    }
    form.clearErrors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  const handleConfirm = (values) => {
    onConfirm(values);
    form.reset();
  };

  const handleClose = () => {
    onClose();
    form.reset();
  };

  return (
    <Dialog
      maxWidth="xs"
      open={open}
      onClose={handleClose}
      sx={{ '.MuiPaper-root': { padding: 0, margin: 0, borderRadius: '12px', width: '100%' } }}
      closeAfterTransition={false}
    >
      <form noValidate className="space-y-6 p-6" onSubmit={form.onSubmit(handleConfirm)}>
        <div className="relative flex">
          <div className="flex items-center space-x-3">
            <h3 className="text-h6 font-semibold">
              {initialValues ? 'Edit Embedded Donation Page' : 'Add Embedded Donation Page'}
            </h3>
          </div>

          <IconButton
            Icon={<IconX size={20} className="text-gray-400" />}
            srOnly="Close"
            onClick={handleClose}
            color="transparent"
            className="absolute -right-1 -top-1 !p-1"
          />
        </div>

        <p className="text-base">
          Add an external page that contains one of your DonorSpring donate button or form embeds. This page will appear
          as a link option in the email editor with tracking parameters automatically appended.
        </p>

        <div className="space-y-4">
          <Input
            {...form.getInputProps('name')}
            label="Page Name"
            placeholder="e.g. Our Website Donate Page"
            size="xl"
            containerClassName="w-full"
            helperText={form.errors.name}
          />
          <Input
            {...form.getInputProps('url')}
            label="Page URL"
            placeholder="https://example.org/donate"
            size="xl"
            containerClassName="w-full"
            helperText={form.errors.url}
          />
        </div>

        <div className="flex justify-end space-x-3">
          <Button title="Cancel" onClick={handleClose} disabled={loading} />
          <Button
            type="submit"
            title={loading ? 'Saving...' : 'Save'}
            color="primary"
            disabled={loading}
            loading={loading}
          />
        </div>
      </form>
    </Dialog>
  );
};

EmbeddedDonationPageModal.propTypes = {
  open: PropTypes.bool.isRequired,
  initialValues: PropTypes.shape({
    name: PropTypes.string,
    url: PropTypes.string,
  }),
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
};

const EmbeddedDonationPages = () => {
  const {
    account: { embeddedDonationPages = [], defaultEmbeddedDonationPageUrl = '' },
    loading,
    saveEmbeddedDonationPages,
    updateEmbeddedDonationPages,
  } = useAccount();
  const [modalOpen, setModalOpen] = useState(false);
  const [editIndex, setEditIndex] = useState(null);

  const save = (pages, defaultUrl) => {
    updateEmbeddedDonationPages({ embeddedDonationPages: pages, defaultEmbeddedDonationPageUrl: defaultUrl });
    saveEmbeddedDonationPages({ embeddedDonationPages: pages, defaultEmbeddedDonationPageUrl: defaultUrl });
  };

  const handleAdd = (values) => {
    const updated = [...embeddedDonationPages, values];
    // If this is the first page, set it as default
    const defaultUrl = embeddedDonationPages.length === 0 ? values.url : defaultEmbeddedDonationPageUrl;
    save(updated, defaultUrl);
    setModalOpen(false);
  };

  const handleEdit = (values) => {
    const oldPage = embeddedDonationPages[editIndex];
    const updated = embeddedDonationPages.map((page, i) => (i === editIndex ? values : page));
    // If the edited page was the default and its URL changed, update the default URL
    const defaultUrl = defaultEmbeddedDonationPageUrl === oldPage.url ? values.url : defaultEmbeddedDonationPageUrl;
    save(updated, defaultUrl);
    setEditIndex(null);
  };

  const handleDelete = (index) => {
    const deletedPage = embeddedDonationPages[index];
    const updated = embeddedDonationPages.filter((_, i) => i !== index);
    // If the deleted page was the default, clear or reassign default
    let defaultUrl = defaultEmbeddedDonationPageUrl;
    if (defaultUrl === deletedPage.url) {
      defaultUrl = updated.length > 0 ? updated[0].url : '';
    }
    save(updated, defaultUrl);
  };

  const handleSetDefault = (index) => {
    save(embeddedDonationPages, embeddedDonationPages[index].url);
  };

  return (
    <div className="relative divide-y divide-gray-100 rounded-lg border border-gray-100 bg-white-100">
      <div className="flex items-center justify-between gap-4 px-4 py-3">
        <div>
          <h2 className="text-h5 font-semibold">Embedded Donation Pages</h2>
          <p className="text-sm">
            Add external landing pages containing your DonorSpring embed. These will appear as link options in the email
            editor with campaign tracking parameters automatically added.
          </p>
        </div>
        <Button
          title="Add Page"
          LeftIcon={IconPlus}
          color="primary"
          onClick={() => setModalOpen(true)}
          className="shrink-0"
        />
      </div>

      {embeddedDonationPages.length > 0 && (
        <div className="divide-y divide-gray-100">
          {embeddedDonationPages.map((page, index) => {
            const isDefault = page.url === defaultEmbeddedDonationPageUrl;
            return (
              <div key={page.url} className={`flex items-center justify-between px-4 py-3`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <p className="font-medium">{page.name}</p>
                    {isDefault && (
                      <span className="rounded bg-primary-50 px-1.5 py-0.5 text-xs font-medium text-primary-700">
                        Default
                      </span>
                    )}
                  </div>
                  <p className="truncate text-sm text-gray-500">{page.url}</p>
                </div>
                <div className="flex items-center space-x-2">
                  <IconButton
                    Icon={
                      isDefault ? (
                        <IconStarFilled size={18} className="text-primary-700" />
                      ) : (
                        <IconStar size={18} className="text-gray-400" />
                      )
                    }
                    srOnly={isDefault ? 'Default page' : 'Set as default'}
                    onClick={() => !isDefault && handleSetDefault(index)}
                    color="transparent"
                    title={isDefault ? 'Default page' : 'Set as default'}
                  />
                  <IconButton
                    Icon={<IconPencil size={18} className="text-gray-500" />}
                    srOnly="Edit"
                    onClick={() => setEditIndex(index)}
                    color="transparent"
                  />
                  <IconButton
                    Icon={<IconTrash size={18} className="text-red-500" />}
                    srOnly="Delete"
                    onClick={() => handleDelete(index)}
                    color="transparent"
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {embeddedDonationPages.length === 0 && (
        <div className={`px-4 py-6 text-center text-sm text-gray-500`}>No embedded donation pages added yet.</div>
      )}

      <EmbeddedDonationPageModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onConfirm={handleAdd}
        loading={loading}
      />

      <EmbeddedDonationPageModal
        open={editIndex !== null}
        initialValues={editIndex !== null ? embeddedDonationPages[editIndex] : null}
        onClose={() => setEditIndex(null)}
        onConfirm={handleEdit}
        loading={loading}
      />
    </div>
  );
};

export default EmbeddedDonationPages;
